import React, { useEffect, useRef, useState } from "react";
import { useQueryClient } from '@tanstack/react-query';
import { unlockQuizWithCoins } from '../../services/quizCoinService';
import { useLocation, useNavigate } from "react-router-dom";
import { SUBSCRIPTION_ROUTES } from "../subscription/subscriptionRoutes";
import { assessmentNavigation } from "../subscription/assessmentNavigation";
import { buildQuizPurchaseState } from "./quizAccess";
import { areRewardedAdsEnabled } from '../../utils/rewardedAds';
import "../../components/ReportModall.css";
import "../../articlesblogs/FilterBlog.css";
import "./QuizPurchaseDialog.css";

// Import SVG

const QuizPurchaseDialog = ({ open, onClose, quiz = {}, navigationState = {}, isLoading = false, onUnlocked, coinBalance = 0 }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const client = useQueryClient();
  const requestPending = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [shortfall, setShortfall] = useState(null);
  const mounted = useRef(true);
  const modalRef = useRef(null);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const quizId = Number(quiz.quizId || quiz.quiz_id || quiz.id || 0);
  useEffect(() => { setError(''); setShortfall(null); }, [open, quizId]);
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement;
    modalRef.current?.querySelector('button')?.focus();
    const keyDown = event => {
      if (event.key === 'Escape' && !requestPending.current) onClose();
      if (event.key !== 'Tab') return;
      const buttons = [...(modalRef.current?.querySelectorAll('button:not(:disabled)') || [])];
      if (!buttons.length) { event.preventDefault(); modalRef.current?.focus(); return; }
      if (event.shiftKey && document.activeElement === buttons[0]) { event.preventDefault(); buttons[buttons.length - 1].focus(); }
      else if (!event.shiftKey && document.activeElement === buttons[buttons.length - 1]) { event.preventDefault(); buttons[0].focus(); }
    };
    document.addEventListener('keydown', keyDown);
    return () => { document.removeEventListener('keydown', keyDown); previous?.focus?.(); };
  }, [open, onClose]);
  if (!open) return null;

  const closeAll = () => {
    if (!requestPending.current) onClose();
  };

  const unlock = async () => {
    if (requestPending.current || isLoading) return;
    requestPending.current = true;
    setBusy(true); setError(''); setShortfall(null);
    try {
      const result = await unlockQuizWithCoins(quizId);
      const updateCache = current => {
        if (!current) return current;
        return { ...current,
          ...(result.balance !== null ? { advertEarnedCoins: result.balance } : {}),
          ...(result.granted && current.quizList ? { quizList: current.quizList.map(item => Number(item.quizId || item.quiz_id || item.id) === quizId
            ? { ...item, paymentStatus: 1, status: 'Available' } : item) } : {}),
        };
      };
      client.setQueriesData({ queryKey: ['quizSeriesList'] }, updateCache);
      client.setQueriesData({ queryKey: ['seriesQuizList'] }, updateCache);
      if (!result.granted) { if (mounted.current) setShortfall(result); return; }
      if (!mounted.current) return;
      onClose();
      onUnlocked?.({ ...quiz, paymentStatus: 1, payment_status: 1 }, result);
      // Navigate before source-list refetching can replace the card with a loader.
      await Promise.all(['quizSeriesList', 'seriesQuizList', 'attemptedQuizList', 'sidebarData'].map(key => client.invalidateQueries({ queryKey: [key] })));
    } catch (requestError) {
      if (mounted.current) setError(requestError.message || 'Unable to unlock quiz.');
    } finally {
      requestPending.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  const subscribe = () => {
    if (isLoading || requestPending.current) return;
    const state = {
      ...buildQuizPurchaseState(quiz, navigationState),
      ...assessmentNavigation({ assessmentReturnTarget: {
        pathname: location.pathname,
        state: { ...location.state, ...navigationState },
      } }),
    };
    onClose();
    navigate(SUBSCRIPTION_ROUTES.assessmentPlans, { state });
  };

  return (
    <div className="modal-overlay resetModal unlockModal delModal" onClick={closeAll}>
      <div
        className="modal-container text-center rounded-4"
        ref={modalRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="quiz-purchase-title"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
           <div className="close-btn2 text-end unlock-close-btn">
              <button
                type="button"
                className="btn-close"
                onClick={closeAll}
                aria-label="Close"
                disabled={busy}
              > </button>
            </div>
        {/* Icon */}
        <div className="premium-icon delete-trash unlockHead">
          <i className="fa-solid fa-award"></i>
        </div>

        {/* Title */}
        <h5 id="quiz-purchase-title" className="fw-bold blackText mb-2">
          {shortfall ? 'More coins needed' : 'Unlock Premium Access'}
        </h5>

        {/* Description */}
        <p className="unlockText mt-2">
         {quiz.subscriptionMessage || quiz.subscription_message || 'Subscribe or use your earned coins to unlock this quiz.'}
        </p>
         <p className="unlockText">
         {shortfall ? `Balance: ${shortfall.eligibleBalance ?? shortfall.balance} coins · Cost: ${shortfall.cost} coins · Remaining: ${shortfall.remaining} coins`
           : `Balance: ${coinBalance} coins${Number(quiz.coinUnlockCost || quiz.coin_unlock_cost) > 0 ? ` · Cost: ${Number(quiz.coinUnlockCost || quiz.coin_unlock_cost)} coins` : ''}`}
        </p>
        {error && <p role="alert">{error}</p>}

        <div className="filter-blog-modal__footer unlockFooter">
            <button
            type="button"
            className="filter-blog-modal__action filter-blog-modal__action--apply activev-btn"
            onClick={subscribe}
            disabled={isLoading || busy}
          >
            <i className="bi bi-rocket-takeoff-fill"></i>
             <span> Subscribe</span>
          </button>

          <button type="button" className="filter-blog-modal__action delete-close watchCoin" onClick={unlock} disabled={busy || isLoading}>
            <i className="fa-solid fa-dollar-sign"></i>
            <span>{busy ? 'Unlocking...' : 'Unlock with Coins'}</span>
          </button>
          {areRewardedAdsEnabled() && <button type="button" className="filter-blog-modal__action delete-close" disabled={busy || isLoading}
            onClick={() => setError('Rewarded ads are not configured for web yet.')}>
            <i className="fa-solid fa-tv" />
            <span>Watch Ads &amp; Earn Coins</span>
          </button>}
          <button type="button" className="filter-blog-modal__action delete-close" onClick={closeAll} disabled={busy}>Cancel</button>
        
        </div>
      </div>
    </div>
  );
};

export default QuizPurchaseDialog;
